import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import { hexToHslString, hslStringToHex } from "../../lib/color";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const COLOR_FIELDS = [
  { key: "background", label: "Background" },
  { key: "foreground", label: "Text" },
  { key: "primary", label: "Primary (Forest Green)" },
  { key: "secondary", label: "Secondary (Rust)" },
  { key: "accent", label: "Accent (Sand)" },
];

const HEADING_FONTS = ["Cormorant Garamond", "Playfair Display", "Fraunces", "DM Serif Display", "Libre Baskerville", "Space Grotesk"];
const BODY_FONTS = ["Outfit", "DM Sans", "Manrope", "Work Sans", "Nunito Sans", "Space Grotesk"];

const PRESETS = [
  { name: "GYBC Classic", background: "45 30% 98%", foreground: "145 25% 15%", primary: "145 35% 17%", secondary: "22 55% 54%", accent: "35 50% 77%", font_heading: "Cormorant Garamond", font_body: "Outfit" },
  { name: "Coastal Sage", background: "80 24% 96%", foreground: "150 18% 18%", primary: "150 22% 30%", secondary: "14 58% 55%", accent: "40 45% 80%", font_heading: "Fraunces", font_body: "Work Sans" },
  { name: "Rosewood", background: "20 40% 97%", foreground: "345 30% 20%", primary: "345 45% 28%", secondary: "18 70% 58%", accent: "10 55% 85%", font_heading: "Playfair Display", font_body: "DM Sans" },
  { name: "Midnight Bloom", background: "260 25% 12%", foreground: "40 30% 92%", primary: "265 40% 55%", secondary: "42 85% 60%", accent: "300 25% 40%", font_heading: "DM Serif Display", font_body: "Manrope" },
  { name: "Mono Ink", background: "40 12% 96%", foreground: "0 0% 10%", primary: "0 0% 12%", secondary: "28 80% 52%", accent: "0 0% 82%", font_heading: "Space Grotesk", font_body: "Space Grotesk" },
  { name: "Sunset Terra", background: "36 44% 96%", foreground: "24 35% 18%", primary: "18 55% 40%", secondary: "28 75% 52%", accent: "80 25% 62%", font_heading: "Libre Baskerville", font_body: "Nunito Sans" },
];

export default function AdminTheme() {
  const [theme, setTheme] = useState(null);

  useEffect(() => { api.get("/theme").then((r) => setTheme(r.data)).catch(() => {}); }, []);

  if (!theme) return <p className="text-muted-foreground">Loading…</p>;

  const currentPreset = PRESETS.find((p) =>
    ["background", "foreground", "primary", "secondary", "accent", "font_heading", "font_body"].every((k) => p[k] === theme[k])
  )?.name;

  const setColor = (key, hex) => setTheme((t) => ({ ...t, [key]: hexToHslString(hex) }));
  const setFont = (key, val) => setTheme((t) => ({ ...t, [key]: val }));
  const applyPreset = (name) => {
    const p = PRESETS.find((x) => x.name === name);
    if (!p) return;
    const { name: _n, ...vals } = p;
    setTheme((t) => ({ ...t, ...vals }));
  };

  const save = async () => {
    try { await api.put("/admin/theme", { value: theme }); toast.success("Theme saved — applied to the public site"); }
    catch { toast.error("Save failed"); }
  };

  return (
    <div data-testid="admin-theme">
      <h1 className="font-display text-3xl md:text-4xl font-medium text-primary mb-2">Theme</h1>
      <p className="text-muted-foreground mb-8">Customize brand colors and fonts. Everything previews live before you save.</p>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-card border border-primary/10 rounded-2xl p-6">
          <div className="mb-6">
            <label className="text-xs text-muted-foreground">Preset theme</label>
            <Select value={currentPreset} onValueChange={applyPreset}>
              <SelectTrigger data-testid="theme-preset" className="mt-1 rounded-xl"><SelectValue placeholder="Choose a predefined theme…" /></SelectTrigger>
              <SelectContent>{PRESETS.map((p) => <SelectItem key={p.name} value={p.name} data-testid={`preset-${p.name.replace(/\s+/g, "-").toLowerCase()}`}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground mt-1.5">Applies a curated color + font combo to the preview. Fine-tune below, then Save &amp; apply.</p>
          </div>
          <h2 className="font-display text-xl font-semibold text-primary mb-4">Colors</h2>
          <div className="grid sm:grid-cols-2 gap-5">
            {COLOR_FIELDS.map((f) => (
              <div key={f.key} className="flex items-center gap-3">
                <input type="color" data-testid={`theme-${f.key}`} value={hslStringToHex(theme[f.key])} onChange={(e) => setColor(f.key, e.target.value)} className="w-12 h-12 rounded-lg border border-primary/20 cursor-pointer bg-transparent" />
                <div>
                  <p className="text-sm font-medium text-primary">{f.label}</p>
                  <p className="text-xs text-muted-foreground font-mono">{theme[f.key]}</p>
                </div>
              </div>
            ))}
          </div>

          <h2 className="font-display text-xl font-semibold text-primary mb-4 mt-8">Fonts</h2>
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="text-xs text-muted-foreground">Heading font</label>
              <Select value={theme.font_heading} onValueChange={(v) => setFont("font_heading", v)}>
                <SelectTrigger data-testid="theme-font-heading" className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>{HEADING_FONTS.map((f) => <SelectItem key={f} value={f}><span style={{ fontFamily: `'${f}'` }}>{f}</span></SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Body font</label>
              <Select value={theme.font_body} onValueChange={(v) => setFont("font_body", v)}>
                <SelectTrigger data-testid="theme-font-body" className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>{BODY_FONTS.map((f) => <SelectItem key={f} value={f}><span style={{ fontFamily: `'${f}'` }}>{f}</span></SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <button onClick={save} data-testid="theme-save" className="mt-8 rounded-full bg-secondary text-secondary-foreground px-6 py-2.5 text-sm font-semibold hover:opacity-90">Save & apply</button>
        </div>

        {/* Live preview */}
        <div className="bg-card border border-primary/10 rounded-2xl p-6" data-testid="theme-preview">
          <p className="text-xs text-muted-foreground mb-3">Live preview</p>
          <div className="rounded-2xl p-6 border border-primary/10" style={{ background: `hsl(${theme.background})`, color: `hsl(${theme.foreground})` }}>
            <p className="text-xs uppercase tracking-[0.25em] font-semibold" style={{ color: `hsl(${theme.secondary})` }}>Where women lift women</p>
            <h3 style={{ fontFamily: `'${theme.font_heading}'`, color: `hsl(${theme.foreground})` }} className="text-4xl font-semibold mt-2 leading-tight">We've got each other's back.</h3>
            <p style={{ fontFamily: `'${theme.font_body}'` }} className="mt-3 text-sm opacity-80">This is how your body copy will read across the site. A community of 500+ women in Toronto and beyond.</p>
            <div className="flex gap-3 mt-5">
              <span style={{ background: `hsl(${theme.primary})`, color: `hsl(${theme.background})`, fontFamily: `'${theme.font_body}'` }} className="rounded-full px-5 py-2 text-sm font-semibold">Join GYBC</span>
              <span style={{ background: `hsl(${theme.secondary})`, color: "#fff", fontFamily: `'${theme.font_body}'` }} className="rounded-full px-5 py-2 text-sm font-semibold">Events</span>
              <span style={{ background: `hsl(${theme.accent})`, color: `hsl(${theme.foreground})`, fontFamily: `'${theme.font_body}'` }} className="rounded-full px-5 py-2 text-sm font-semibold">Member</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
